<template>
  <div class="gestion-contactos">
    <!-- Header de la sección -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">
        <feather-icon icon="UsersIcon" size="18" class="mr-2" />
        Contactos de la Empresa
      </h5>
      <b-button 
        variant="outline-primary" 
        size="sm" 
        @click="agregarContacto"
        :disabled="!idEmpresa"
      >
        <feather-icon icon="PlusIcon" size="14" class="mr-1" />
        Agregar Contacto
      </b-button>
    </div>

    <!-- Lista de contactos -->
    <div v-if="cargando" class="text-center py-3">
      <b-spinner small variant="primary" />
      <span class="ml-2">Cargando contactos...</span>
    </div>

    <div v-else-if="contactos.length === 0" class="text-center py-4 text-muted">
      <feather-icon icon="UsersIcon" size="48" class="mb-2" />
      <p class="mb-0">No hay contactos registrados para esta empresa</p>
      <small>Haz clic en "Agregar Contacto" para crear el primero</small>
    </div>

    <div v-else>
      <b-card 
        v-for="contacto in contactos" 
        :key="contacto.idContacto" 
        class="mb-2 contacto-card"
      >
        <div class="d-flex justify-content-between align-items-start">
          <div class="flex-grow-1">
            <div class="d-flex align-items-center mb-2">
              <b-avatar
                :text="contacto.nombre.charAt(0).toUpperCase()"
                variant="primary"
                size="sm"
                class="mr-2"
              />
              <div>
                <h6 class="mb-0">{{ contacto.nombre }}</h6>
                <small class="text-muted" v-if="contacto.cargo">{{ contacto.cargo }}</small>
              </div>
            </div>
            
            <div class="contacto-info">
              <div v-if="contacto.telefono" class="mb-1">
                <feather-icon icon="PhoneIcon" size="14" class="mr-1" />
                <a :href="`tel:${contacto.telefono}`" class="text-decoration-none">
                  {{ contacto.telefono }}
                </a>
              </div>
              <div v-if="contacto.email" class="mb-1">
                <feather-icon icon="MailIcon" size="14" class="mr-1" />
                <a href="#" @click.prevent="enviarEmailContacto(contacto)" class="text-decoration-none text-primary" style="cursor: pointer;">
                  {{ contacto.email }}
                </a>
              </div>
              <div v-if="contacto.linkedin" class="mb-1">
                <feather-icon icon="LinkedinIcon" size="14" class="mr-1" />
                <a :href="normalizarLinkedInUrl(contacto.linkedin)" target="_blank" rel="noopener noreferrer" class="text-decoration-none">
                  LinkedIn
                  <feather-icon icon="ExternalLinkIcon" size="12" class="ml-1" />
                </a>
              </div>
              <div v-if="contacto.observaciones" class="mt-2">
                <small class="text-muted">{{ contacto.observaciones }}</small>
              </div>
            </div>
          </div>
          
          <div class="contacto-acciones">
            <b-button-group size="sm">
              <b-button 
                variant="outline-info" 
                @click="verDetalle(contacto)"
                v-b-tooltip.hover
                title="Ver detalle del contacto"
              >
                <feather-icon icon="EyeIcon" size="14" />
              </b-button>
              <b-button 
                variant="outline-primary" 
                @click="editarContacto(contacto)"
                v-b-tooltip.hover
                title="Editar contacto"
              >
                <feather-icon icon="EditIcon" size="14" />
              </b-button>
              <b-button 
                variant="outline-danger" 
                @click="eliminarContacto(contacto)"
                v-b-tooltip.hover
                title="Eliminar contacto"
              >
                <feather-icon icon="TrashIcon" size="14" />
              </b-button>
            </b-button-group>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Modal para crear/editar contacto -->
    <b-modal
      :id="modalId"
      :title="modoEdicion ? 'Editar Contacto' : 'Nuevo Contacto'"
      size="lg"
      @show="onModalShow"
      @hidden="ocultarFormulario"
    >
      <b-form @submit.prevent="guardarContacto">
        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre *" :invalid-feedback="errores.nombre" :state="!errores.nombre">
              <b-form-input
                v-model="formulario.nombre"
                placeholder="Nombre completo del contacto"
                :state="!errores.nombre"
                required
              />
            </b-form-group>

            <b-form-group label="Cargo" :invalid-feedback="errores.cargo" :state="!errores.cargo">
              <b-form-input
                v-model="formulario.cargo"
                placeholder="Cargo o posición en la empresa"
                :state="!errores.cargo"
              />
            </b-form-group>

            <b-form-group label="Teléfono" :invalid-feedback="errores.telefono" :state="!errores.telefono">
              <b-form-input
                v-model="formulario.telefono"
                placeholder="Número de teléfono"
                :state="!errores.telefono"
              />
            </b-form-group>
          </b-col>

          <b-col md="6">
            <b-form-group label="Email" :invalid-feedback="errores.email" :state="!errores.email">
              <b-form-input
                v-model="formulario.email"
                type="email"
                placeholder="Correo electrónico"
                :state="!errores.email"
              />
            </b-form-group>

            <b-form-group label="LinkedIn" :invalid-feedback="errores.linkedin" :state="!errores.linkedin">
              <b-form-input
                v-model="formulario.linkedin"
                placeholder="URL del perfil de LinkedIn"
                :state="!errores.linkedin"
              />
            </b-form-group>

            <b-form-group label="Observaciones">
              <b-form-textarea
                v-model="formulario.observaciones"
                placeholder="Observaciones adicionales"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="ocultarFormulario">
          Cancelar
        </b-button>
        <b-button 
          variant="primary" 
          @click="guardarContacto"
          :disabled="guardando"
        >
          <b-spinner v-if="guardando" small class="mr-1" />
          {{ guardando ? 'Guardando...' : (modoEdicion ? 'Actualizar' : 'Crear') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { ContactoService } from '@/services/contactoService'
import { Contacto } from '@/types'

export default {
  name: 'GestionContactos',
  props: {
    idEmpresa: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      contactos: [],
      cargando: false,
      mostrarFormulario: false,
      modoEdicion: false,
      guardando: false,
      formulario: {
        idContacto: null,
        nombre: '',
        cargo: '',
        telefono: '',
        email: '',
        linkedin: '',
        observaciones: ''
      },
      errores: {}
    }
  },
  computed: {
    modalId() {
      return `modal-contacto-${this.idEmpresa}`
    }
  },
  watch: {
    idEmpresa: {
      handler() {
        if (this.idEmpresa) {
          this.cargarContactos()
        }
      },
      immediate: true
    }
  },
  methods: {
    async cargarContactos() {
      if (!this.idEmpresa) return
      
      this.cargando = true
      try {
        this.contactos = await ContactoService.getByEmpresa(this.idEmpresa)
      } catch (error) {
        this.$toast.error('Error al cargar los contactos')
        console.error('Error al cargar contactos:', error)
        this.contactos = []
      } finally {
        this.cargando = false
      }
    },
    resetFormulario() {
      this.formulario = {
        idContacto: null,
        nombre: '',
        cargo: '',
        telefono: '',
        email: '',
        linkedin: '',
        observaciones: ''
      }
      this.errores = {}
      this.modoEdicion = false
    },
    agregarContacto() {
      this.modoEdicion = false
      this.resetFormulario()
      this.mostrarFormulario = true
      this.$bvModal.show(this.modalId)
    },
    verDetalle(contacto) {
      this.$router.push({ 
        name: 'contactos-detalle', 
        params: { id: contacto.idContacto } 
      })
    },
    editarContacto(contacto) {
      this.modoEdicion = true
      // Copiar todos los campos del contacto al formulario
      this.formulario = {
        idContacto: contacto.idContacto,
        nombre: contacto.nombre || '',
        cargo: contacto.cargo || '',
        telefono: contacto.telefono || '',
        email: contacto.email || '',
        linkedin: contacto.linkedin || '',
        observaciones: contacto.observaciones || ''
      }
      this.mostrarFormulario = true
      this.$bvModal.show(this.modalId)
    },
    onModalShow() {
      // Solo resetear si no estamos en modo edición
      if (!this.modoEdicion) {
        this.resetFormulario()
      }
    },
    ocultarFormulario() {
      this.mostrarFormulario = false
      this.$bvModal.hide(this.modalId)
    },
    normalizarLinkedInUrl(url) {
      if (!url) return ''
      
      // Si ya tiene protocolo, devolver tal cual
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      
      // Si empieza con linkedin.com o www.linkedin.com, agregar https://
      if (url.startsWith('linkedin.com/') || url.startsWith('www.linkedin.com/')) {
        return `https://${url}`
      }
      
      // Si solo es un perfil (ej: "in/juan-perez" o "juan-perez"), construir la URL completa
      const perfil = url.replace(/^\/+/, '') // Quitar barras iniciales
      if (perfil.startsWith('in/')) {
        return `https://www.linkedin.com/${perfil}`
      }
      
      // Si no empieza con "in/", asumir que es un nombre de perfil
      return `https://www.linkedin.com/in/${perfil}`
    },
    validarFormulario() {
      this.errores = {}
      let esValido = true

      // Validar nombre
      if (!this.formulario.nombre || this.formulario.nombre.trim() === '') {
        this.errores.nombre = 'El nombre es obligatorio'
        esValido = false
      }

      // Validar email si se proporciona
      if (this.formulario.email && this.formulario.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(this.formulario.email)) {
          this.errores.email = 'El formato del email no es válido'
          esValido = false
        }
      }

      return esValido
    },
    async guardarContacto() {
      if (!this.validarFormulario()) {
        return
      }

      this.guardando = true
      try {
        const datosContacto = {
          ...this.formulario,
          idEmpresa: this.idEmpresa,
          nombre: this.formulario.nombre.trim(),
          cargo: this.formulario.cargo.trim() || null,
          telefono: this.formulario.telefono.trim() || null,
          email: this.formulario.email.trim() || null,
          linkedin: this.formulario.linkedin.trim() || null,
          observaciones: this.formulario.observaciones.trim() || null
        }

        let contactoGuardado
        if (this.modoEdicion) {
          contactoGuardado = await ContactoService.update(this.formulario.idContacto, datosContacto)
          this.$toast.success('Contacto actualizado correctamente')
          this.$emit('contacto-actualizado', contactoGuardado)
        } else {
          contactoGuardado = await ContactoService.create(datosContacto)
          this.$toast.success('Contacto creado correctamente')
          this.$emit('contacto-creado', contactoGuardado)
        }

        this.ocultarFormulario()
        await this.cargarContactos()
      } catch (error) {
        this.$toast.error('Error al guardar el contacto')
        console.error('Error al guardar contacto:', error)
      } finally {
        this.guardando = false
      }
    },
    async eliminarContacto(contacto) {
      const confirmacion = await this.$bvModal.msgBoxConfirm(
        `¿Estás seguro de que quieres eliminar el contacto "${contacto.nombre}"?`,
        {
          title: 'Confirmar eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      )

      if (confirmacion) {
        try {
          await ContactoService.delete(contacto.idContacto)
          this.$toast.success('Contacto eliminado correctamente')
          this.$emit('contacto-eliminado', contacto)
          await this.cargarContactos()
        } catch (error) {
          this.$toast.error('Error al eliminar el contacto')
          console.error('Error al eliminar contacto:', error)
        }
      }
    },
    enviarEmailContacto(contacto) {
      this.$emit('enviar-email-contacto', contacto)
    }
  }
}
</script>

<style scoped>
.contacto-card {
  border-left: 4px solid #007bff;
  transition: all 0.2s ease;
}

.contacto-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.contacto-info {
  font-size: 0.9rem;
}

.contacto-info a {
  color: #007bff;
}

.contacto-info a:hover {
  color: #0056b3;
}

.contacto-acciones {
  flex-shrink: 0;
}
</style>
